/**
 * Modal that opens a remote shell (PowerShell or CMD) on a connected agent.
 *
 * Flow:
 *   1. Mint a viewer token for the session (same auth as screen viewing)
 *   2. Build the viewer WebSocket URL
 *   3. Render the ShellPanel connected to that URL
 */

import { useEffect, useMemo, useState } from "react";
import type { Session } from "../../api/types";
import { Button } from "../../components/ui/Button";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { ErrorState } from "../../components/ui/States";
import { ShellPanel, type ShellType } from "../shell";
import { useMintViewerToken } from "./hooks";

interface ShellSessionModalProps {
  session: Session | null;
  onClose: () => void;
}

/**
 * Build the viewer relay URL. The token is deliberately NOT in the query
 * string (GC10) -- it travels in the WebSocket subprotocol offer instead, so
 * it never reaches proxy logs, the relay access log, or browser history.
 */
function buildViewerWsUrl(sessionId: string): string {
  const wsProto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const qs = new URLSearchParams({
    session_id: sessionId,
    viewer_name: "Shell",
  });
  return `${wsProto}//${window.location.host}/ws/viewer?${qs.toString()}`;
}

export function ShellSessionModal({ session, onClose }: ShellSessionModalProps) {
  const open = session != null;
  const mint = useMintViewerToken();
  const [shellType, setShellType] = useState<ShellType>("powershell");
  const [started, setStarted] = useState(false);

  // Mint a viewer token when the modal opens
  useEffect(() => {
    if (open && session) {
      mint.mutate(session.id);
      setStarted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, session?.id]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStarted(false);
    }
  }, [open]);

  const shellId = useMemo(
    () => `shell-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    // Regenerate when the modal opens fresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session?.id, open],
  );

  const wsUrl = useMemo(() => {
    if (!mint.data || !session) return null;
    return buildViewerWsUrl(session.id);
  }, [mint.data, session]);

  const wsToken = mint.data?.token ?? null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Remote Shell -- ${session?.agent_name ?? ""}`}
      wide
    >
      {mint.isPending ? (
        <div style={{ padding: 24, textAlign: "center" }}>
          <Spinner />
          <p style={{ marginTop: 8, color: "var(--text-muted)" }}>
            Authenticating...
          </p>
        </div>
      ) : mint.isError ? (
        <ErrorState
          title="Could not open shell"
          message={
            mint.error instanceof Error
              ? mint.error.message
              : "Failed to authenticate for this session."
          }
          action={
            <Button
              variant="primary"
              onClick={() => session && mint.mutate(session.id)}
            >
              Retry
            </Button>
          }
        />
      ) : !started ? (
        <div style={{ padding: 24 }}>
          <p style={{ marginBottom: 16, color: "var(--text)" }}>
            Open a remote shell on <strong>{session?.agent_name}</strong>
          </p>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <Button
              variant={shellType === "powershell" ? "primary" : "ghost"}
              onClick={() => setShellType("powershell")}
            >
              PowerShell
            </Button>
            <Button
              variant={shellType === "cmd" ? "primary" : "ghost"}
              onClick={() => setShellType("cmd")}
            >
              CMD
            </Button>
          </div>
          <Button variant="primary" onClick={() => setStarted(true)}>
            Open {shellType === "cmd" ? "CMD" : "PowerShell"}
          </Button>
        </div>
      ) : wsUrl && wsToken ? (
        <div style={{ height: 500 }}>
          <ShellPanel
            wsUrl={wsUrl}
            wsToken={wsToken}
            shellId={shellId}
            shellType={shellType}
            onClose={() => onClose()}
          />
        </div>
      ) : null}
    </Modal>
  );
}
